import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AffiliateToolkitProduct, StudioMarketingIndex } from "@itaas/pep-contracts";
import { getAffiliateToolkit } from "../../api/affiliate-api";
import {
  campaignLabel,
  getAssetsForProduct,
  getPrimaryFile,
  getStudioMarketingIndex,
} from "../../api/studio-marketing";
import { StudioMarketingSection } from "../marketing/StudioMarketingSection";
import { buildAffiliateUrl } from "../../utils/affiliate-attribution";
import { getPepSiteUrl } from "../../config";

function DemoCodeGenerator() {
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState("edu");
  const demoCode = `${picked.slice(0, 3).toUpperCase()}-A1B2C3`;
  const demoLink = buildAffiliateUrl(getPepSiteUrl(), `/configure?product=${picked}`, demoCode);

  const steps = [
    "Choisissez le produit à promouvoir (ex. Edu, RH, Inventory…).",
    "Inscrivez-vous sur la page ambassadeur — c'est gratuit.",
    "Recevez votre code unique (format PRODUIT-XXXXXX).",
    "Partagez le lien ?ref=VOTRE_CODE sur WhatsApp, Facebook ou en présentiel.",
  ];

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <h3 className="h5 fw-bold mb-3">Démo — comment obtenir votre code d&apos;affiliation</h3>
        <ol className="small text-secondary mb-3">
          {steps.map((text, index) => (
            <li key={text} className={index === step ? "fw-semibold text-body" : ""}>
              {text}
            </li>
          ))}
        </ol>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {["edu", "rh", "inventory", "hospitol", "billing"].map((slug) => (
            <button
              key={slug}
              type="button"
              className={`btn btn-sm ${picked === slug ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => {
                setPicked(slug);
                setStep(0);
              }}
            >
              {slug.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="p-3 rounded-3 bg-body-tertiary small mb-3">
          <div className="mb-1">
            <span className="text-secondary">Code exemple :</span>{" "}
            <code className="user-select-all">{demoCode}</code>
          </div>
          <div>
            <span className="text-secondary">Lien partageable :</span>{" "}
            <code className="user-select-all text-break">{demoLink}</code>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => setStep((s) => Math.min(s + 1, steps.length - 1))}
          >
            Étape suivante
          </button>
          <Link className="btn btn-primary btn-sm" to={`/affiliation/inscription?product=${picked}`}>
            Créer mon vrai code →
          </Link>
        </div>
      </div>
    </div>
  );
}

export function AffiliateToolkitSection({ defaultProduct = "edu" }: { defaultProduct?: string }) {
  const [items, setItems] = useState<AffiliateToolkitProduct[]>([]);
  const [studioIndex, setStudioIndex] = useState<StudioMarketingIndex | null>(null);
  const [campaignProduct, setCampaignProduct] = useState(defaultProduct);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    Promise.all([getAffiliateToolkit(), getStudioMarketingIndex()])
      .then(([toolkit, index]) => {
        setItems(toolkit.products);
        setStudioIndex(index);
      })
      .catch(console.error);
  }, []);

  const marketingByProduct = useMemo(() => {
    if (!studioIndex) return new Map<string, ReturnType<typeof getAssetsForProduct>>();
    return new Map(
      items.map((item) => [item.productSlug, getAssetsForProduct(studioIndex, item.productSlug)]),
    );
  }, [items, studioIndex]);

  const productsWithAssets = useMemo(
    () => items.filter((item) => (marketingByProduct.get(item.productSlug) ?? []).length > 0),
    [items, marketingByProduct],
  );

  const visible = items.filter((item) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return item.productSlug.includes(q) || item.displayNameFr.toLowerCase().includes(q);
  });

  return (
    <section className="mb-5" id="outils-marketing">
      <nav className="d-flex flex-wrap gap-2 mb-4 pb-3 border-bottom">
        <a className="btn btn-sm btn-primary" href="#kit-campagne">
          Kit campagne
        </a>
        <a className="btn btn-sm btn-outline-secondary" href="#liens-rapides">
          Liens rapides
        </a>
        <a className="btn btn-sm btn-outline-secondary" href="#code-ambassadeur">
          Mon code
        </a>
      </nav>

      <div id="kit-campagne" className="mb-5">
        <div
          className="rounded-4 p-4 p-md-5 mb-4 text-white"
          style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)" }}
        >
          <p className="text-uppercase small fw-semibold mb-1 opacity-75">Kit campagne</p>
          <h2 className="h4 fw-bold mb-2">Assets prêts à partager</h2>
          <p className="mb-3 small opacity-90">
            Flyers print, posts LinkedIn, stories et vidéos — téléchargez et diffusez avec votre
            lien ambassadeur.
          </p>
          <div className="d-flex flex-wrap gap-1">
            {(productsWithAssets.length ? productsWithAssets : items).map((item) => (
              <button
                key={item.productSlug}
                type="button"
                className={`btn btn-sm ${campaignProduct === item.productSlug ? "btn-warning" : "btn-outline-light"}`}
                onClick={() => setCampaignProduct(item.productSlug)}
              >
                {item.displayNameFr}
              </button>
            ))}
          </div>
        </div>
        <StudioMarketingSection productSlug={campaignProduct} hideHeader />
      </div>

      <div id="liens-rapides" className="mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
          <div>
            <h3 className="h5 fw-bold mb-1">Liens rapides par produit</h3>
            <p className="text-secondary small mb-0">
              Fiche produit, essai gratuit et visuels de secours.
            </p>
          </div>
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: "14rem" }}
            placeholder="Filtrer un produit…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        <div className="row g-3">
          {visible.map((item) => {
            const marketing = marketingByProduct.get(item.productSlug) ?? [];
            const socialPost = marketing.find((a) => a.contentType === "social-post");
            const primaryVisual = socialPost ? getPrimaryFile(socialPost) : undefined;
            const storyUrl = primaryVisual?.url ?? item.storyImageUrl;
            const storyLabel = socialPost ? campaignLabel(socialPost.campaignId) : "Visuel";

            return (
              <div className="col-md-6 col-lg-4" key={item.productSlug}>
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-3 d-flex flex-column">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <img
                        src={item.logoUrl}
                        alt=""
                        width={36}
                        height={36}
                        className="rounded"
                        style={{ objectFit: "contain" }}
                      />
                      <h4 className="h6 fw-bold mb-0">{item.displayNameFr}</h4>
                    </div>
                    <div className="mt-auto d-flex flex-wrap gap-1">
                      <Link to={item.productPageUrl} className="btn btn-outline-secondary btn-sm">
                        Fiche
                      </Link>
                      <Link to={item.trialUrl} className="btn btn-outline-primary btn-sm">
                        Essai
                      </Link>
                      <a
                        href={storyUrl}
                        download
                        className="btn btn-outline-secondary btn-sm"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {storyLabel}
                      </a>
                    </div>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </div>

      <div id="code-ambassadeur">
        <DemoCodeGenerator />
      </div>
    </section>
  );
}
